import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于被窝影院',
    description: '被窝影院品牌介绍、口碑评价与联系方式，高清电影电视剧免费在线观看平台。',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '被窝影院',
    url: 'https://www.beiwoyingyuan.com',
    description: '被窝影院 - 高清电影电视剧免费在线观看平台，提供海量影视资源，支持APP下载和在线观看。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'service@beiwoyingyuan.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '被窝影院是否免费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，被窝影院提供完全免费的影视内容在线观看服务，无需注册会员即可畅享海量高清电影电视剧资源。',
        },
      },
      {
        '@type': 'Question',
        name: '被窝影院APP如何下载？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '被窝影院APP提供Android和iOS版本下载，您可以在官网首页或本页面找到下载入口。APP支持离线缓存功能，随时随地观看。',
        },
      },
      {
        '@type': 'Question',
        name: '被窝影院画质如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '被窝影院提供高清甚至超清画质的影视内容，支持多种清晰度选择，不缓冲不卡顿，为您带来流畅的观影体验。',
        },
      },
      {
        '@type': 'Question',
        name: '被窝影院最新地址在哪里？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '被窝影院最新地址请收藏本站首页。建议同时下载APP，更加稳定便捷，不受地址变更影响。',
        },
      },
    ],
  };

  return (
    <>
      <SEO
        title="关于我们 - 被窝影院品牌介绍、口碑评价与联系方式"
        description="了解被窝影院品牌故事与口碑评价，获取被窝影院联系方式。被窝影院专注提供高清电影电视剧免费在线观看服务，支持APP下载。"
        keywords="被窝影院,关于被窝影院,被窝影院品牌,被窝影院口碑,被窝影院联系方式,被窝影院APP下载"
        canonicalUrl="https://www.beiwoyingyuan.com/about"
        schema={{ ...aboutSchema, ...orgSchema, ...faqSchema }}
      />

      <Navbar />

      <main>
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="w-full px-4 md:px-6 pt-6 pb-10">
          <div className="max-w-[1400px] mx-auto">
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-100 mb-2">
              关于被窝影院
            </h1>
            <p className="text-sm text-foreground-500 mb-8">
              被窝影院官网 - 高清电影电视剧免费在线观看平台
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <article className="p-6 rounded-xl bg-background-900/50 border border-background-800/50">
                  <h2 className="text-lg font-heading font-bold text-foreground-100 mb-4">品牌介绍</h2>
                  <div className="space-y-3 text-sm text-foreground-400 leading-relaxed">
                    <p>
                      <strong className="text-foreground-200">被窝影院</strong>成立于2024年，是国内领先的在线影视平台之一。我们致力于为广大用户提供高清、流畅、免费的影视观看体验，让每一位用户都能在"被窝"里享受极致的观影乐趣。
                    </p>
                    <p>
                      平台汇集了海量<strong className="text-foreground-200">电影</strong>、<strong className="text-foreground-200">电视剧</strong>、<strong className="text-foreground-200">动漫</strong>和<strong className="text-foreground-200">综艺节目</strong>资源，涵盖国内外热门大片、经典老片、热播剧集和最新番剧。无论您喜欢动作大片、温馨喜剧、烧脑悬疑还是热血动漫，被窝影院都能满足您的观影需求。
                    </p>
                    <p>
                      被窝影院采用先进的视频压缩与传输技术，确保<strong className="text-foreground-200">高清画质不缓冲</strong>。同时，我们提供<strong className="text-foreground-200">被窝影院APP</strong>下载服务，支持Android和iOS双平台，让您随时随地畅享观影。
                    </p>
                    <p>
                      作为被窝影院的用户，您无需注册或付费即可访问全部内容。我们相信，优质的影视内容应该对所有人开放。被窝影院最新地址请收藏本站首页，我们会持续维护更新，确保服务稳定。
                    </p>
                  </div>
                </article>

                <article className="p-6 rounded-xl bg-background-900/50 border border-background-800/50">
                  <h2 className="text-lg font-heading font-bold text-foreground-100 mb-4">口碑评价</h2>
                  <div className="space-y-4">
                    {[
                      {
                        name: '影迷小王',
                        rating: 5,
                        text: '被窝影院真的是宝藏网站！资源超全，画质清晰，而且完全免费，追剧党的福音。APP也很好用，离线缓存功能太贴心了！',
                      },
                      {
                        name: '追剧达人',
                        rating: 5,
                        text: '用了被窝影院半年多了，更新速度很快，新片上线几乎同步。最满意的是不卡顿，看高清电影体验很棒。',
                      },
                      {
                        name: '动漫爱好者',
                        rating: 4,
                        text: '国漫日漫资源很丰富，凡人修仙传、鬼灭之刃都有高清版。希望后续能增加更多老番资源就更完美了。',
                      },
                      {
                        name: '家庭观影',
                        rating: 5,
                        text: '家里老人小孩都喜欢在被窝影院看剧，操作简单，内容健康，还能投屏到电视上看。推荐给身边朋友了！',
                      },
                    ].map((review, i) => (
                      <div key={i} className="p-4 rounded-lg bg-background-800/40">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-label font-semibold text-foreground-200">
                            {review.name}
                          </span>
                          <div className="flex items-center gap-0.5">
                            {Array.from({ length: 5 }).map((_, j) => (
                              <i
                                key={j}
                                className={`text-xs ${
                                  j < review.rating
                                    ? 'ri-star-fill text-primary-400'
                                    : 'ri-star-line text-foreground-600'
                                }`}
                              ></i>
                            ))}
                          </div>
                        </div>
                        <p className="text-sm text-foreground-400 leading-relaxed">{review.text}</p>
                      </div>
                    ))}
                  </div>
                </article>

                <article className="p-6 rounded-xl bg-background-900/50 border border-background-800/50">
                  <h2 className="text-lg font-heading font-bold text-foreground-100 mb-4">常见问题</h2>
                  <div className="space-y-3">
                    {[
                      { q: '被窝影院是否免费？', a: '是的，被窝影院提供完全免费的影视内容在线观看服务，无需注册会员即可畅享全部资源。' },
                      { q: '被窝影院APP如何下载？', a: '您可以在官网首页找到Android和iOS下载入口，点击对应按钮即可下载安装。' },
                      { q: '被窝影院画质如何？', a: '我们提供高清甚至超清画质的影视内容，支持多种清晰度选择，确保流畅不卡顿。' },
                      { q: '被窝影院最新地址在哪里？', a: '请收藏本站首页，我们会持续更新确保正常访问。建议同时下载APP，更加稳定便捷。' },
                      { q: '如何联系被窝影院客服？', a: '您可以通过邮箱 service@beiwoyingyuan.com 与我们联系，我们会在24小时内回复。' },
                    ].map((faq, i) => (
                      <details key={i} className="group border border-background-700/30 rounded-lg">
                        <summary className="flex items-center justify-between p-4 cursor-pointer text-sm font-label text-foreground-200 hover:text-foreground-100 transition-colors">
                          {faq.q}
                          <i className="ri-arrow-down-s-line text-base group-open:rotate-180 transition-transform"></i>
                        </summary>
                        <p className="px-4 pb-4 text-sm text-foreground-500 leading-relaxed">
                          {faq.a}
                        </p>
                      </details>
                    ))}
                  </div>
                </article>
              </div>

              <aside className="space-y-6">
                <div className="p-6 rounded-xl bg-background-900/50 border border-background-800/50">
                  <h3 className="text-base font-heading font-bold text-foreground-100 mb-4">联系方式</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/10">
                        <i className="ri-mail-line text-sm text-primary-400"></i>
                      </span>
                      <div>
                        <p className="text-xs text-foreground-500">电子邮箱</p>
                        <p className="text-sm text-foreground-200">service@beiwoyingyuan.com</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-500/10">
                        <i className="ri-wechat-line text-sm text-accent-400"></i>
                      </span>
                      <div>
                        <p className="text-xs text-foreground-500">微信公众号</p>
                        <p className="text-sm text-foreground-200">被窝影院</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-500/10">
                        <i className="ri-weibo-line text-sm text-secondary-400"></i>
                      </span>
                      <div>
                        <p className="text-xs text-foreground-500">微博</p>
                        <p className="text-sm text-foreground-200">@被窝影院官方</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-xl bg-gradient-to-br from-primary-500/10 to-accent-500/10 border border-primary-500/10">
                  <h3 className="text-base font-heading font-bold text-foreground-100 mb-3">
                    下载被窝影院APP
                  </h3>
                  <p className="text-sm text-foreground-400 mb-4">
                    随时随地，畅享高清观影体验。支持离线缓存，不缓冲不卡顿。
                  </p>
                  <div className="space-y-2">
                    <a
                      href="#"
                      className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-full bg-primary-500 text-background-950 text-sm font-label font-semibold hover:bg-primary-400 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-android-line"></i>
                      Android 下载
                    </a>
                    <a
                      href="#"
                      className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-full border border-foreground-300/30 text-foreground-100 text-sm font-label hover:bg-foreground-50/5 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-apple-line"></i>
                      iOS 下载
                    </a>
                  </div>
                </div>

                <div className="p-6 rounded-xl bg-background-900/50 border border-background-800/50">
                  <h3 className="text-base font-heading font-bold text-foreground-100 mb-4">快速入口</h3>
                  <div className="flex flex-col gap-2">
                    <Link to="/category/movie" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors flex items-center gap-2">
                      <i className="ri-film-line text-xs"></i>
                      电影大全
                    </Link>
                    <Link to="/category/tv" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors flex items-center gap-2">
                      <i className="ri-tv-line text-xs"></i>
                      电视剧大全
                    </Link>
                    <Link to="/category/anime" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors flex items-center gap-2">
                      <i className="ri-emotion-line text-xs"></i>
                      动漫大全
                    </Link>
                    <Link to="/category/variety" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors flex items-center gap-2">
                      <i className="ri-movie-line text-xs"></i>
                      综艺大全
                    </Link>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}